import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { getChat, listMessages } from "./api";
import { fetchJobsByMessages, type JobsByMessageMap } from "./jobsApi";
import type { Chat, ChatMessage, MessageListResponse } from "./types";

export const chatDetailKey = (chatId: string | undefined) => ["v2", "chat", chatId ?? ""] as const;
export const chatMessagesKey = (chatId: string | undefined) =>
    ["v2", "chat", chatId ?? "", "messages"] as const;
export const chatJobsKey = (chatId: string | undefined) =>
    ["v2", "artifacts", "jobs", "by-chat", chatId ?? ""] as const;

export function useActiveChat(chatId: string | undefined) {
    return useQuery<Chat>({
        queryKey: chatDetailKey(chatId),
        queryFn: () => {
            if (!chatId) throw new Error("chatId is required");
            return getChat(chatId);
        },
        enabled: !!chatId,
        staleTime: 30_000,
    });
}

/**
 * Размер одной порции ленты. Это же значение — потолок бэкенда
 * (`CursorPaginationParams.limit`, `le=100`), больше запросить нельзя.
 */
export const MESSAGE_PAGE_SIZE = 100;

/**
 * Склеивает порции ленты в хронологию.
 *
 * Каждая порция приходит от новых к старым (`order: "desc"`), а каждая
 * следующая — старше предыдущей. Значит склеенный список тоже идёт от новых к
 * старым, и разворот один на всю ленту. Дедуп по id — страховка на случай, если
 * сообщение переедет через границу порции (рефетч после отправки сдвигает хвост).
 */
export function mergeMessagePages(pages: MessageListResponse[]): MessageListResponse {
    const seen = new Set<string>();
    const newestFirst: ChatMessage[] = [];
    for (const page of pages) {
        for (const message of page.messages) {
            if (seen.has(message.id)) continue;
            seen.add(message.id);
            newestFirst.push(message);
        }
    }
    return {
        messages: newestFirst.reverse(),
        // `total` считается бэкендом по всему чату и одинаков во всех порциях;
        // берём из первой, она есть всегда.
        total: pages[0]?.total ?? 0,
    };
}

export function useChatMessages(chatId: string | undefined) {
    return useInfiniteQuery({
        queryKey: chatMessagesKey(chatId),
        queryFn: async ({ pageParam }) => {
            if (!chatId) throw new Error("chatId is required");
            // Просим ПОСЛЕДНИЕ сообщения и разворачиваем в хронологию, которую
            // ждёт лента. При `order: "asc"` лимит отрезал чат по началу: в
            // переписке длиннее лимита только что отправленное сообщение не
            // попадало в выдачу вовсе — отправка выглядела как «ничего не
            // произошло», и человек жал «Сгенерировать» повторно, оплачивая
            // каждое нажатие.
            return listMessages(chatId, {
                limit: MESSAGE_PAGE_SIZE,
                order: "desc",
                ...(pageParam ? { before: pageParam } : {}),
            });
        },
        initialPageParam: undefined as string | undefined,
        // Курсор следующей порции — время самого раннего из уже полученных:
        // бэкенд отдаёт строго более ранние (`created_at < before`). Неполная
        // порция значит, что начало чата достигнуто.
        getNextPageParam: (lastPage) =>
            lastPage.messages.length < MESSAGE_PAGE_SIZE
                ? undefined
                : lastPage.messages[lastPage.messages.length - 1]?.createdAt,
        select: (data) => mergeMessagePages(data.pages),
        enabled: !!chatId,
        staleTime: 5_000,
    });
}

// Last generation job (with artifacts + provider/price meta) per message_id.
// Single query keyed by chat to avoid one network call per message bubble.
export function useChatJobsByMessages(
    chatId: string | undefined,
    messageIds: string[],
): { jobs: JobsByMessageMap; isLoading: boolean } {
    // The list of message_ids is the actual cache key — sort to make it stable
    // across React Query renders even if the messages array order shifts.
    const sortedIds = [...messageIds].sort();
    const enabled = !!chatId && sortedIds.length > 0;
    const query = useQuery({
        queryKey: [...chatJobsKey(chatId), sortedIds] as const,
        queryFn: () => fetchJobsByMessages(sortedIds),
        enabled,
        staleTime: 0,
    });
    return { jobs: query.data?.jobs ?? {}, isLoading: query.isLoading };
}
